import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/commercial-roof-repair";
const NAME = "Commercial Roof Repair";
const TITLE = "Commercial Roof Repair Phoenix, AZ | Same-Week Service | Durafoam";
const DESCRIPTION =
  "Phoenix commercial roof repair on foam, tile, TPO, modified bitumen, and BUR. Same-week response, written diagnosis, honest repair-vs-recoat-vs-replace guidance since 1989.";

const faqs = [
  {
    q: "How quickly can you respond to a commercial roof leak?",
    a: "Active commercial leaks get same-day or next-day response most weeks. For emergencies where inventory or tenant operations are at risk, see our emergency roof leak repair service — we prioritize commercial actives ahead of routine work.",
  },
  {
    q: "Do you repair TPO, modified bitumen, and built-up (BUR) roofs?",
    a: "Yes — we inspect, repair, and recoat all three even though we don't install them new. We rebuild flashings around HVAC curbs and drains, repair seam failures and punctures, fix ponding-related blistering, and restore reflective coatings. When one of these systems reaches end of life, we'll walk you through why foam is almost always the better long-term reroof.",
  },
  {
    q: "How much does commercial roof repair cost in Phoenix?",
    a: "Targeted commercial repairs typically run $500–$3,500 depending on scope — flashing rebuilds, drain repairs, and seam failures on the low end; larger ponding fixes and multi-penetration rework on the higher end. See our commercial roofing cost guide for full ranges by system type.",
  },
  {
    q: "How do I know if I need repair, recoat, or full replacement?",
    a: "Repair fixes isolated problems on a sound roof; recoat resets a weathered surface where the substrate is intact; replacement is only appropriate when the substrate itself is saturated or structurally gone. We inspect first, document with photos, and give you all three numbers when they honestly apply — so you make the call, not us.",
  },
  {
    q: "Do you provide written condition reports?",
    a: "Yes. Every commercial inspection closes with a written report: photos, findings, recommended repairs with pricing, and a plain-language assessment of remaining service life. Suitable for reserve accounting, board approvals, or insurance claims.",
  },
  {
    q: "Do you handle warranty and insurance claim documentation?",
    a: "Yes — we coordinate directly with roof system manufacturers on warranty repairs and provide the photo documentation, scope narratives, and invoicing carriers need for storm-damage claims.",
  },
  {
    q: "Can you repair the roof while tenants are operating?",
    a: "Almost always. We schedule noisy or odor-producing work around your operating hours, coordinate access with facilities, and keep loading docks and customer entrances clear. Tenant disruption is one of the first things we plan around.",
  },
  {
    q: "Do you offer commercial roof maintenance contracts?",
    a: "Yes — twice-a-year inspections, drain and scupper cleaning, coating touch-ups, and photo-documented condition reports. A maintenance contract typically extends real service life 5–10 years beyond spec and keeps warranties in force. Especially valuable for property managers and multi-property portfolios.",
  },
  {
    q: "Are you licensed and bonded for commercial roof repair?",
    a: "Yes — Arizona ROC commercial license, commercial general liability, and workers' comp. COIs naming your entity as additional insured available before mobilization.",
  },
];

export const Route = createFileRoute("/commercial-roof-repair")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: NAME,
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="commercial-roof-repair"
      eyebrow="Commercial"
      h1="Commercial Roof Repair in Phoenix, AZ"
      subhead="Phoenix commercial roof repair on every major system — foam, tile, shingle, TPO, modified bitumen, and BUR. Same-week response, written diagnosis, and honest repair-vs-recoat-vs-replace guidance since 1989."
      heroBullets={[
        "Same-week response on commercial active leaks",
        "Foam, tile, shingle, TPO, mod-bit & BUR repairs",
        "Written photo diagnosis + repair-vs-recoat pricing",
        "Property manager & portfolio maintenance contracts",
      ]}
      whatIs={{
        heading: "What we repair on Phoenix commercial roofs",
        body: (
          <>
            <p>
              Most commercial roof calls in Phoenix don't need replacement — they need honest
              triage. A leak is almost always a symptom of a specific failure (a flashing, a
              drain, a seam, a penetration) rather than the whole roof giving up. We diagnose the
              real issue, photograph it, and price the repair against the reality of your roof's
              remaining service life.
            </p>
            <h3 className="mt-8 text-xl font-bold text-primary">Systems we repair</h3>
            <ul>
              <li>
                <strong>Foam (SPF) roofs.</strong> Bubbles, blisters, coating failures, bird
                pecks, hail damage, and coating end-of-life recoats. See{" "}
                <Link
                  to="/foam-roof-repair-phoenix"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  foam roof repair
                </Link>{" "}
                and{" "}
                <Link
                  to="/foam-roof-coating-recoating"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  foam recoating
                </Link>
                .
              </li>
              <li>
                <strong>TPO (thermoplastic polyolefin).</strong> Seam separations, punctures,
                failed terminations, and flashing rebuilds around HVAC curbs and drains. We
                repair TPO honestly — but when the membrane reaches end of life, we recommend
                converting to foam rather than another TPO install.
              </li>
              <li>
                <strong>Modified bitumen (mod-bit).</strong> Blister repair, seam separations,
                flashing failures, and field-coating touch-ups. Once the asphalt reaches end of
                life, a foam-over reroof usually pencils out better than a second mod-bit.
              </li>
              <li>
                <strong>Built-up roofing (BUR / tar-and-gravel).</strong> Targeted repairs, drain
                rebuilds, and coating maintenance while the built-up is still serviceable. When
                it isn't, foam is the reroof.
              </li>
              <li>
                <strong>Tile & shingle commercial roofs.</strong> Underlayment repair, tile
                reset, storm-damaged shingle sections, and flashing rebuilds on pitched
                commercial buildings.
              </li>
            </ul>
          </>
        ),
      }}
      climate={{
        heading: "What commercial roofs typically fail on in Phoenix",
        body: (
          <>
            <p>
              Since 1989, diagnosing commercial leaks across the Valley, the failures repeat.
              Here's where we spend most of our repair time:
            </p>
            <ul>
              <li>
                <strong>HVAC curbs & rooftop equipment.</strong> The single most common commercial
                leak source. Original flashings weather, get scuffed by service techs, or were
                never sealed correctly. We rebuild these to last.
              </li>
              <li>
                <strong>Drains & scuppers.</strong> Debris clogs cause standing water; failed
                drain flashings let it into the deck. Cleaning plus flashing rebuild is a
                high-ROI repair on almost every commercial roof.
              </li>
              <li>
                <strong>Seam failures on single-ply.</strong> TPO and PVC seams age, especially
                where foot traffic crosses them. We heat-weld or seal per manufacturer spec.
              </li>
              <li>
                <strong>Ponding water.</strong> Standing water accelerates every failure mode.
                Where the ponding is fixable we fix it; where it isn't, we document it as a
                recoat/reroof planning item.
              </li>
              <li>
                <strong>Parapet caps & scupper transitions.</strong> Wind-driven monsoon rain
                exploits every gap. We reseal, replace caps, and re-flash where needed.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our commercial repair process",
        steps: [
          {
            title: "Same-week inspection & photo diagnosis",
            body: "Senior repair tech walks the roof, locates the actual leak source (not just the interior stain), and photo-documents every issue found. For active leaks we prioritize response within 24–48 hours most weeks.",
          },
          {
            title: "Written scope with repair-vs-recoat pricing",
            body: "You get a written report with photos, findings, and honest pricing. When a recoat would solve more than the repair does, we quote both. When replacement is genuinely the right call, we say so and price that too.",
          },
          {
            title: "Repair with tenant coordination",
            body: "Scheduled around your operating hours. Crews coordinate roof access with facilities, keep loading docks clear, and never block customer entrances. Daily progress if the scope runs more than a day.",
          },
          {
            title: "Workmanship warranty & follow-up",
            body: "Every repair carries our workmanship warranty. For property managers and multi-property portfolios we offer maintenance contracts that catch problems before they turn into repair calls.",
          },
        ],
      }}
      whyChoose={{
        heading: "Repair vs. recoat vs. replacement — how we decide",
        body: (
          <>
            <ul>
              <li>
                <strong>Repair.</strong> Isolated leak, sound substrate, real service life left in
                the rest of the roof. This is 70% of what we do on commercial buildings.
              </li>
              <li>
                <strong>Recoat.</strong> Coating has weathered off, reflectivity is gone, and
                minor surface cracking is showing — but the foam or bitumen underneath is intact.
                A fresh elastomeric coating restores waterproofing at 20–30% of replacement cost
                and adds 10–15 years. See{" "}
                <Link
                  to="/commercial-roof-coating"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  commercial roof coating
                </Link>
                .
              </li>
              <li>
                <strong>Replacement.</strong> Substrate is saturated, structurally compromised, or
                so patched it's no longer serviceable. When this is the honest call, foam is
                almost always our reroof recommendation — see{" "}
                <Link
                  to="/commercial-foam-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  commercial foam roofing
                </Link>
                .
              </li>
            </ul>
            <p>
              For real Phoenix commercial repair pricing, see the{" "}
              <Link
                to="/commercial-roofing-cost-phoenix"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                commercial roofing cost guide
              </Link>
              .
            </p>
          </>
        ),
      }}
      extraSection={{
        heading: "Also for commercial owners",
        body: (
          <>
            <ul>
              <li>
                Active leak right now?{" "}
                <Link
                  to="/emergency-roof-leak-repair"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  Emergency roof leak repair
                </Link>{" "}
                — same-day response most weeks.
              </li>
              <li>
                Warehouse, distribution center, or large low-slope building? See{" "}
                <Link
                  to="/warehouse-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  warehouse roofing
                </Link>
                .
              </li>
              <li>
                Managing multiple properties?{" "}
                <Link
                  to="/apartment-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  Apartment & condo
                </Link>{" "}
                and{" "}
                <Link
                  to="/hoa-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  HOA roofing
                </Link>{" "}
                cover our multi-property workflow.
              </li>
              <li>
                Full commercial services overview:{" "}
                <Link
                  to="/commercial-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  commercial roofing
                </Link>
                .
              </li>
            </ul>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Commercial roof leaking, ponding, or overdue for a real diagnosis?"
      ctaBody="Call us or request a free commercial inspection. We'll walk the roof, locate the actual failure, and hand you a written report with repair, recoat, and (if honestly warranted) replacement pricing side by side."
    />
  );
}
